import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatEatDateTime, formatMoney, moneyFromCents } from '@wodi/domain';
import type { TheatreCaseView, TheatreChargeView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  Notice,
  Row,
  Table,
  TableBody,
  TableHeader,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { cancelTheatreCharge, recordTheatreCharge } from '../api/theatre.ts';
import { useOnline } from '../hooks/use-online.ts';
import { toasts } from '../toasts.ts';
import { priceWords, TheatreItemPicker, type PickedItem } from './item-picker.tsx';
import styles from './theatre.module.css';
import { errorText } from './words.ts';

// Theatre charges (docs/plans/phase2-theatre.md §1.1 item 8): the theatre fee, charged from
// the case at sign in, and theatre services used, each charged as it is recorded. A line
// recorded in error is cancelled with a reason; a billed one needs a credit note.

const CHARGE_WORDS: Record<NonNullable<TheatreChargeView['chargeStatus']>, string> = {
  pending: 'Waiting to be billed',
  billed: 'On a bill',
  cancelled: 'Cancelled',
};

function AddServiceDialog({ view, onClose }: { view: TheatreCaseView; onClose: () => void }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [item, setItem] = useState<PickedItem | null>(null);
  const [quantity, setQuantity] = useState('1');
  const count = Number(quantity);
  const countOk = Number.isInteger(count) && count >= 1 && count <= 100;
  const add = useMutation({
    mutationFn: () =>
      recordTheatreCharge(view.id, {
        clientRequestId,
        serviceItemId: item?.serviceItemId ?? '',
        quantity: count,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['theatre'] });
      toasts.show({ title: 'Service recorded', description: item?.name ?? '' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Add service used"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {item === null ? (
        <TheatreItemPicker
          label="Theatre service"
          visitId={view.visit.id}
          purpose="service"
          onSelect={setItem}
          autoFocus
        />
      ) : (
        <p className={styles.itemTitle}>
          <span className={`wodi-id ${styles.code}`}>{item.code}</span>
          {`${item.name}, ${priceWords(item.price)} `}
          <Button
            dense
            variant="quiet"
            onPress={() => {
              setItem(null);
            }}
          >
            Change
          </Button>
        </p>
      )}
      <TextField
        label="Quantity"
        inputMode="numeric"
        width="xs"
        value={quantity}
        onChange={setQuantity}
        {...(quantity !== '' && !countOk ? { errorMessage: 'Between 1 and 100.' } : {})}
      />
      <p className={styles.meta}>
        It is charged to the visit now. Consumables are charged when they are issued from stock.
      </p>
      {add.error === null ? null : (
        <Notice tone="error">
          {errorText(add.error, "The service wasn't recorded. Try again.")}
        </Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!online || item === null || !countOk}
          isPending={add.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            add.mutate();
          }}
        >
          Record service
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CancelLineDialog({ line, onClose }: { line: TheatreChargeView; onClose: () => void }) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const cancel = useMutation({
    mutationFn: () => cancelTheatreCharge(line.id ?? '', reason.trim()),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['theatre'] });
      toasts.show({
        title: 'Service cancelled',
        description:
          result.charge === 'needs_credit_note'
            ? 'It is already on a bill: ask for a credit note'
            : 'Its charge is cancelled',
      });
      onClose();
    },
  });
  return (
    <Dialog
      title="Cancel service"
      tone="alert"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <p className={styles.meta}>{`${line.item.name}: only for a service recorded in error.`}</p>
      <TextArea label="Why was it recorded in error?" value={reason} onChange={setReason} />
      {cancel.error === null ? null : (
        <Notice tone="error">
          {errorText(cancel.error, "The service wasn't cancelled. Try again.")}
        </Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Keep service
        </Button>
        <Button
          variant="destructive"
          isDisabled={!online || reason.trim().length < 5}
          isPending={cancel.isPending}
          pendingLabel="Cancelling…"
          onPress={() => {
            cancel.mutate();
          }}
        >
          Cancel service
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ChargesSection({ view, mayRecord }: { view: TheatreCaseView; mayRecord: boolean }) {
  const online = useOnline();
  const [adding, setAdding] = useState(false);
  const [cancelling, setCancelling] = useState<TheatreChargeView | null>(null);
  const canAdd = mayRecord && (view.status === 'in_theatre' || view.status === 'completed');
  return (
    <>
      <Table flat dense aria-label="Theatre charges">
        <TableHeader>
          <Column isRowHeader>Charge</Column>
          <Column>Quantity</Column>
          <Column>Recorded</Column>
          <Column>Billing</Column>
          <Column>Amount</Column>
          <Column>Action</Column>
        </TableHeader>
        <TableBody
          items={view.charges.map((c, i) => ({ ...c, key: c.id ?? `fee-${String(i)}` }))}
          renderEmptyState={() =>
            'No theatre charges yet. The theatre fee is charged when sign in is recorded.'
          }
        >
          {(c) => (
            <Row id={c.key}>
              <Cell>
                <span className={`wodi-id ${styles.code}`}>{c.item.code}</span>
                {c.kind === 'theatre_fee' ? `${c.item.name} (theatre fee)` : c.item.name}
              </Cell>
              <Cell>
                <span className={styles.money}>{String(c.quantity)}</span>
              </Cell>
              <Cell>{`${formatEatDateTime(new Date(c.recordedAt))}, ${c.recordedBy.displayName}`}</Cell>
              <Cell>
                {c.status === 'cancelled'
                  ? `Cancelled${c.cancelReason === null ? '' : `: ${c.cancelReason}`}`
                  : c.chargeStatus === null
                    ? 'Not charged'
                    : CHARGE_WORDS[c.chargeStatus]}
              </Cell>
              <Cell>
                <span className={styles.money}>
                  {c.total === null
                    ? 'Not priced'
                    : formatMoney(moneyFromCents(BigInt(c.total.cents)))}
                </span>
              </Cell>
              <Cell>
                {c.kind === 'service' && c.status === 'active' && mayRecord ? (
                  <Button
                    dense
                    variant="quiet"
                    isDisabled={!online}
                    onPress={() => {
                      setCancelling(c);
                    }}
                  >
                    Cancel line
                  </Button>
                ) : (
                  ''
                )}
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
      {canAdd ? (
        <div className={styles.actions}>
          <Button
            isDisabled={!online}
            onPress={() => {
              setAdding(true);
            }}
          >
            Add service used
          </Button>
        </div>
      ) : null}
      {adding ? (
        <AddServiceDialog
          view={view}
          onClose={() => {
            setAdding(false);
          }}
        />
      ) : null}
      {cancelling === null ? null : (
        <CancelLineDialog
          line={cancelling}
          onClose={() => {
            setCancelling(null);
          }}
        />
      )}
    </>
  );
}
